const chapter = {
  id: 'semiconductors-diodes',
  slug: 'semiconductors-diodes',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Semiconductors & Diodes',
        hindi: 'अर्धचालक और डायोड',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '1–2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 1-2 प्रश्न',
  },
  syllabusTags: ['Electronics', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'classification',
      title: {
        english: 'Classification of Solids by Conductivity',
        hindi: 'चालकता द्वारा ठोसों का वर्गीकरण',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Material', 'Resistivity ($\\Omega$·m)', 'Energy gap $E_g$', 'Examples'],
          rows: [
            ['Conductor', '$10^{-8}$–$10^{-2}$', 'No gap (overlapping bands)', 'Cu, Ag, Al'],
            ['Semiconductor', '$10^{-5}$–$10^{6}$', '$\\le 3$ eV', 'Si (1.1 eV), Ge (0.7 eV), GaAs'],
            ['Insulator', '$10^{8}$–$10^{19}$', '$> 3$ eV', 'Diamond, glass'],
          ],
        },
        {
          type: 'note',
          text: {
            english: '**Band theory**: Valence band (filled), conduction band (empty/partial), forbidden gap $E_g$ between them. Electrons jump VB→CB by absorbing energy ≥ $E_g$, leaving **holes** in VB.',
        hindi: '**बैंड सिद्धांत**: वैलेंस बैंड (भरा हुआ), चालन बैंड (empty/partial), उनके बीच निषिद्ध अंतराल $E_g$। इलेक्ट्रॉन ऊर्जा को अवशोषित करके VB→CB पर छलांग लगाते हैं ≥ $E_g$, जिससे VB में **छेद** निकल जाता है।',
          },
        },
      ],
    },
    {
      id: 'intrinsic-extrinsic',
      title: {
        english: 'Intrinsic & Extrinsic Semiconductors',
        hindi: 'नैज और बाह्य अर्धचालक',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Intrinsic** (pure Si, Ge): $n_e = n_h = n_i$. Conductivity rises with $T$ (more bonds break).',
        hindi: '**आंतरिक** (शुद्ध सी, जीई): $n_e = n_h = n_i$। $T$ के साथ चालकता बढ़ती है (अधिक बंधन टूटते हैं)।',
            },
            {
              english: '**Extrinsic** (doped): conductivity hugely enhanced by impurities — only ~1 in $10^6$.',
        hindi: '**बाहरी** (डोप्ड): अशुद्धियों द्वारा अत्यधिक बढ़ी हुई चालकता - $10^6$ में केवल ~1।',
            },
            {
              english: '**n-type**: doped with **pentavalent** (P, As, Sb). Donor level just below CB. Majority: electrons; minority: holes.',
        hindi: '**एन-प्रकार**: **पेंटावेलेंट** (पी, एएस, एसबी) के साथ मिलाया गया। दाता स्तर सीबी के ठीक नीचे। बहुमत: इलेक्ट्रॉन; अल्पसंख्यक: छेद.',
            },
            {
              english: '**p-type**: doped with **trivalent** (B, Al, In, Ga). Acceptor level just above VB. Majority: holes; minority: electrons.',
        hindi: '**पी-प्रकार**: **त्रिकोणीय** (बी, अल, इन, गा) के साथ डोप किया गया। स्वीकर्ता स्तर VB के ठीक ऊपर। बहुमत: छेद; अल्पसंख्यक: इलेक्ट्रॉन.',
            },
            {
              english: '**Mass action law**: $n_e\\, n_h = n_i^2$ (always, intrinsic or extrinsic).',
        hindi: '**सामूहिक कार्रवाई कानून**: $n_e\\, n_h = n_i^2$ (हमेशा, आंतरिक या बाहरी)।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Conductivity',
        hindi: 'प्रवाहकत्त्व',
          },
          statement: {
            english: '$\\sigma = e(n_e \\mu_e + n_h \\mu_h)$ where $\\mu_e, \\mu_h$ are mobilities. Total current $I = I_e + I_h$.',
        hindi: '$\\sigma = e(n_e \\mu_e + n_h \\mu_h)$ जहां $\\mu_e, \\mu_h$ गतिशीलता हैं। कुल वर्तमान $I = I_e + I_h$.',
          },
        },
        {
          type: 'tip',
          text: {
            english: 'Doped semiconductor remains **electrically neutral overall** — extra electrons (n-type) or holes (p-type) are balanced by ionised donor/acceptor sites.',
        hindi: 'डोप किया गया अर्धचालक **कुल मिलाकर विद्युत रूप से तटस्थ** रहता है - अतिरिक्त इलेक्ट्रॉन (एन-प्रकार) या छेद (पी-प्रकार) आयनित donor/acceptor साइटों द्वारा संतुलित होते हैं।',
          },
        },
      ],
    },
    {
      id: 'pn-junction',
      title: {
        english: 'p–n Junction & Diode',
        hindi: 'p–n संधि और डायोड',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'When p- and n-type are joined, electrons diffuse n→p, holes p→n, leaving immobile ionised donors/acceptors near the junction → **depletion region** with built-in **barrier potential** $V_b$ (~0.3 V for Ge, ~0.7 V for Si).',
        hindi: 'जब p- और n-प्रकार जुड़ते हैं, तो इलेक्ट्रॉन n→p, छिद्र p→n को फैलाते हैं, जंक्शन के पास स्थिर आयनित donors/acceptors छोड़ते हैं → **कमी क्षेत्र** अंतर्निहित **अवरोध क्षमता** $V_b$ (Ge के लिए ~0.3 V, Si के लिए ~0.7 V) के साथ।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Forward bias** (p-side at +V): depletion region narrows, barrier reduced to $V_b - V$, diffusion current dominates → exponential rise in current after $V_{\\text{knee}} \\approx V_b$.',
        hindi: '**फॉरवर्ड बायस** (+V पर पी-साइड): कमी क्षेत्र संकीर्ण हो जाता है, बाधा $V_b - V$ तक कम हो जाती है, प्रसार धारा हावी हो जाती है → $V_{\\text{knee}} \\approx V_b$ के बाद धारा में तेजी से वृद्धि होती है। **रिवर्स बायस** (-V पर पी-साइड): कमी क्षेत्र चौड़ा हो जाता है, बाधा $V_b - V$ तक बढ़ जाती है, अल्पसंख्यक वाहकों का केवल छोटा **बहाव धारा** (~µA) - लगभग स्थिर **रिवर्स संतृप्ति धारा** $V_{\\text{knee}} \\approx V_b$।',
            },
            {
              english: '**Reverse bias** (p-side at −V): depletion region widens, barrier increased to $V_b + V$, only tiny **drift current** of minority carriers (~µA) — nearly constant **reverse saturation current** $I_s$.',
        hindi: '**रिवर्स बायस** (पी-साइड −V पर): क्षय क्षेत्र चौड़ा हो जाता है, अवरोध $V_b + V$ तक बढ़ जाता है, अल्पसंख्यक वाहकों का केवल छोटा **बहाव धारा** (~µA) - लगभग स्थिर **रिवर्स संतृप्ति धारा** $I_s$।',
            },
            {
              english: 'At very high reverse voltage, **breakdown** occurs (avalanche or Zener mechanism).',
        hindi: 'बहुत उच्च रिवर्स वोल्टेज पर, **ब्रेकडाउन** होता है (हिमस्खलन या जेनर तंत्र)।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Diode equation (Shockley)',
        hindi: 'डायोड समीकरण (शॉकले)',
          },
          statement: {
            english: '$I = I_s\\left(e^{eV/kT} - 1\\right)$.',
        hindi: '$I = I_s\\left(e^{eV/kT} - 1\\right)$.',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Dynamic resistance',
        hindi: 'गतिशील प्रतिरोध',
          },
          body: {
            english: '$r_d = \\Delta V/\\Delta I$ — slope of $V$–$I$ curve at operating point. Forward $r_d$ small (few Ω); reverse $r_d$ very large (MΩ).',
        hindi: '$r_d = \\Delta V/\\Delta I$ - परिचालन बिंदु पर $V$–$I$ वक्र का ढलान। फ़ॉरवर्ड $r_d$ छोटा (कुछ Ω); रिवर्स $r_d$ बहुत बड़ा (MΩ)। **हाफ-वेव रेक्टिफायर** (1 डायोड): केवल +ve हाफ-साइकल पर संचालित होता है। $r_d = \\Delta V/\\Delta I$, $V$. रिपल $r_d$.',
          },
        },
      ],
    },
    {
      id: 'rectifier',
      title: {
        english: 'Rectifiers & Filter',
        hindi: 'दिष्टकारी और फ़िल्टर',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Half-wave rectifier** (1 diode): conducts only on +ve half-cycle. $I_{dc} = I_m/\\pi$, $I_{rms} = I_m/2$. Ripple frequency = supply $f$.',
        hindi: '**अर्ध-तरंग दिष्टकारी** (1 डायोड): केवल +ve अर्ध-चक्र पर संचालित होता है। $I_{dc} = I_m/\\pi$, $I_{rms} = I_m/2$. तरंग आवृत्ति = आपूर्ति $f$।',
            },
            {
              english: '**Full-wave rectifier** (2 diodes + centre-tap, or 4-diode bridge): both half-cycles used. $I_{dc} = 2I_m/\\pi$, $I_{rms} = I_m/\\sqrt 2$. Ripple frequency = $2f$.',
        hindi: '**फुल-वेव रेक्टिफायर** (2 डायोड + सेंटर-टैप, या 4-डायोड ब्रिज): दोनों आधे-चक्र का उपयोग किया जाता है। $I_{dc} = 2I_m/\\pi$, $I_{rms} = I_m/\\sqrt 2$. रिपल frequency = .',
            },
            {
              english: '**Rectifier efficiency**: $\\eta_{HW} = 40.6\\%$, $\\eta_{FW} = 81.2\\%$.',
        hindi: '**दिष्टकारी दक्षता**: $\\eta_{HW} = 40.6\\%$, $\\eta_{FW} = 81.2\\%$।',
            },
            {
              english: '**Filter** (capacitor across load) smooths pulsating output to nearly DC; full-wave gives much smaller ripple.',
        hindi: '**फ़िल्टर** (लोड पर संधारित्र) लगभग डीसी तक स्पंदित आउटपुट को सुचारू करता है; पूर्ण-तरंग बहुत छोटी तरंग देती है।',
            },
          ],
        },
      ],
    },
    {
      id: 'special-diodes',
      title: {
        english: 'Zener, LED, Photodiode, Solar Cell',
        hindi: 'ज़ेनर, LED, फोटोडायोड, सौर सेल',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Zener diode**: heavily doped ⇒ very thin depletion region. Operates in reverse breakdown region as **voltage regulator** — voltage across it remains nearly constant ($V_Z$) over wide current range.',
        hindi: '**जेनर डायोड**: भारी डोप्ड ⇒ बहुत पतला क्षय क्षेत्र। रिवर्स ब्रेकडाउन क्षेत्र में **वोल्टेज रेगुलेटर** के रूप में काम करता है - इसके पार वोल्टेज व्यापक वर्तमान सीमा पर लगभग स्थिर ($V_Z$) रहता है।',
            },
            {
              english: '**LED** (Light-Emitting Diode): forward-biased junction made of GaAs/GaP/GaN; recombination releases photon of energy $h\\nu \\approx E_g$. Colour set by $E_g$.',
        hindi: '**एलईडी** (लाइट-एमिटिंग डायोड): GaAs/GaP/GaN से बना फॉरवर्ड-बायस्ड जंक्शन; पुनर्संयोजन से ऊर्जा का फोटॉन $h\\nu \\approx E_g$ निकलता है। रंग $E_g$ द्वारा सेट किया गया।',
            },
            {
              english: '**Photodiode**: reverse-biased; light incident on junction generates e–h pairs ⇒ photocurrent ∝ intensity. Used in light meters, optical receivers.',
        hindi: '**फोटोडायोड**: रिवर्स-बायस्ड; जंक्शन पर आपतित प्रकाश ई-एच जोड़े ⇒ प्रकाश धारा ∝ तीव्रता उत्पन्न करता है। प्रकाश मीटर, ऑप्टिकल रिसीवर में उपयोग किया जाता है।',
            },
            {
              english: '**Solar cell**: same physics, **no external bias**; light creates carriers, junction field separates them, generating EMF. Open-circuit voltage $V_{OC}$, short-circuit current $I_{SC}$.',
        hindi: '**सौर सेल**: समान भौतिकी, **कोई बाहरी पूर्वाग्रह नहीं**; प्रकाश वाहक बनाता है, जंक्शन क्षेत्र उन्हें अलग करता है, जिससे ईएमएफ उत्पन्न होता है। ओपन-सर्किट वोल्टेज $V_{OC}$, शॉर्ट-सर्किट करंट $I_{SC}$।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'A Si diode ($V_b = 0.7$ V) is in series with a 1 kΩ resistor across a 5 V battery. Find current.',
        hindi: 'एक Si डायोड ($V_b = 0.7$ V) 5 V बैटरी में 1 kΩ अवरोधक के साथ श्रृंखला में है। वर्तमान खोजें.',
          },
          solution: {
            english: '$I = (5 - 0.7)/1000 = 4.3$ mA.',
        hindi: '$I = (5 - 0.7)/1000 = 4.3$ mA.',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'In a half-wave rectifier, peak current is $\\pi$ mA. Find $I_{dc}$.',
        hindi: 'हाफ-वेव रेक्टिफायर में, पीक करंट $\\pi$ mA है। $I_{dc}$ खोजें।',
          },
          solution: {
            english: '$I_{dc} = I_m/\\pi = 1$ mA.',
        hindi: '$I_{dc} = I_m/\\pi = 1$ mA.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'n_e n_h = n_i^2',
      desc: {
        english: 'Mass action law',
        hindi: 'सामूहिक कार्रवाई कानून',
      },
    },
    {
      latex: '\\sigma = e(n_e\\mu_e + n_h\\mu_h)',
      desc: {
        english: 'Conductivity',
        hindi: 'प्रवाहकत्त्व',
      },
    },
    {
      latex: 'I = I_s(e^{eV/kT} - 1)',
      desc: {
        english: 'Shockley diode equation',
        hindi: 'शॉक्ले डायोड समीकरण',
      },
    },
    {
      latex: 'I_{dc}^{HW} = I_m/\\pi',
      desc: {
        english: 'Half-wave DC',
        hindi: 'हाफ-वेव डीसी',
      },
    },
    {
      latex: 'I_{dc}^{FW} = 2I_m/\\pi',
      desc: {
        english: 'Full-wave DC',
        hindi: 'फुल-वेव डीसी',
      },
    },
    {
      latex: '\\eta_{FW} = 81.2\\%',
      desc: {
        english: 'Full-wave efficiency',
        hindi: 'पूर्ण-तरंग दक्षता',
      },
    },
    {
      latex: 'h\\nu \\approx E_g\\ \\text{(LED)}',
      desc: {
        english: 'Photon emitted in LED',
        hindi: 'एलईडी में उत्सर्जित फोटॉन',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Diode',
      problem: {
        english: 'In a forward biased Si diode with knee 0.7 V and 5 V supply across 1 kΩ resistor, current?',
        hindi: '1 kΩ अवरोधक पर घुटने 0.7 V और 5 V आपूर्ति के साथ एक अग्रगामी पक्षपाती Si डायोड में, करंट?',
      },
      answer: {
        english: '$4.3$ mA',
        hindi: '$4.3$ mA',
      },
      solution: {
        english: 'Standard.',
        hindi: 'मानक।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Doping',
      problem: {
        english: 'Pentavalent impurity gives which type of semiconductor?',
        hindi: 'पेंटावेलेंट अशुद्धता किस प्रकार का अर्धचालक देती है?',
      },
      answer: {
        english: 'n-type',
        hindi: 'एन-प्रकार',
      },
      solution: {
        english: 'Each donor contributes one extra electron.',
        hindi: 'प्रत्येक दाता एक अतिरिक्त इलेक्ट्रॉन का योगदान देता है।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Mass action',
      problem: {
        english: 'In Si crystal $n_i = 1.5\\times 10^{10}/$cm³. After P-doping $n_e = 5\\times 10^{16}/$cm³. $n_h$?',
        hindi: 'Si क्रिस्टल में $n_i = 1.5\\times 10^{10}/$cm³. पी-डोपिंग के बाद $n_e = 5\\times 10^{16}/$cm³. $n_h$?',
      },
      answer: {
        english: '$4.5\\times 10^3/$cm³',
        hindi: '$4.5\\times 10^3/$cm³',
      },
      solution: {
        english: '$n_h = n_i^2/n_e = (1.5\\times 10^{10})^2/(5\\times 10^{16})$.',
        hindi: '$n_h = n_i^2/n_e = (1.5\\times 10^{10})^2/(5\\times 10^{16})$.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Rectifier',
      problem: {
        english: 'Ripple frequency of full-wave rectifier with 50 Hz supply?',
        hindi: '50 हर्ट्ज आपूर्ति के साथ फुल-वेव रेक्टिफायर की तरंग आवृत्ति?',
      },
      answer: {
        english: '$100$ Hz',
        hindi: '$100$ हर्ट्ज',
      },
      solution: {
        english: '$2f$.',
        hindi: '$2f$।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Zener',
      problem: {
        english: 'Zener diode is operated in:',
        hindi: 'जेनर डायोड संचालित होता है:',
      },
      answer: {
        english: 'Reverse breakdown region',
        hindi: 'रिवर्स ब्रेकडाउन क्षेत्र',
      },
      solution: {
        english: 'Voltage regulation.',
        hindi: 'वोल्टेज विनियमन।',
      },
    },
  ],
  next: {
    slug: 'transistors-logic-gates',
    title: 'Transistors & Logic Gates',
  },
};

export default chapter;
